// Rachel's Room Configuration
// Stock Wig Inventory Management System

/**
 * Default rooms that are created when a new stock wig is added
 * Rooms are processed in sortOrder sequence
 */
export const STOCK_WIG_ROOMS = [
  {
    roomNo: '9001',
    roomName: 'Wig Prepare 1',
    description: 'Bag all wig components',
    location: 'Pomona',
    parentRoomNo: '900',
    sortOrder: 1,
    canDelete: false, // Prep room is required - cannot be deleted
    defaultTask: 'Prepare wig components for assembly'
  },
  {
    roomNo: '1001',
    roomName: 'Machine Room Wefts',
    description: 'Machine weft attachment',
    location: 'Pomona',
    parentRoomNo: '100',
    sortOrder: 2,
    canDelete: true,
    defaultTask: 'Attach wefts using machine'
  },
  {
    roomNo: '1002',
    roomName: 'Machine Room LaceTop',
    description: 'Machine lace top construction',
    location: 'Pomona',
    parentRoomNo: '100',
    sortOrder: 3,
    canDelete: true,
    defaultTask: 'Construct lace top section'
  },
  {
    roomNo: '1003',
    roomName: 'Lining Room',
    description: 'Wig lining installation',
    location: 'Pomona',
    parentRoomNo: '100',
    sortOrder: 4,
    canDelete: true,
    defaultTask: 'Install wig lining'
  },
  {
    roomNo: '3001',
    roomName: 'Hand Sew Room 1',
    description: 'Hand sewn details',
    location: 'Pomona',
    parentRoomNo: '300',
    sortOrder: 5,
    canDelete: true,
    defaultTask: 'Hand sew finishing details'
  },
  {
    roomNo: '10001',
    roomName: 'Quality Control Room 1',
    description: 'Final QC inspection',
    location: 'Pomona',
    parentRoomNo: '1000',
    sortOrder: 6,
    canDelete: true,
    defaultTask: 'Quality control inspection'
  },
  {
    roomNo: '54001',
    roomName: 'China Warehouse',
    description: 'Ship to China warehouse',
    location: 'China',
    parentRoomNo: '5400',
    sortOrder: 7,
    canDelete: true,
    defaultTask: 'Prepare for China shipment'
  },
  {
    roomNo: '51001',
    roomName: 'China Nely',
    description: 'Ship to China Nely warehouse',
    location: 'China',
    parentRoomNo: '5100',
    sortOrder: 8,
    canDelete: true,
    defaultTask: 'Prepare for China Nely shipment'
  },
  {
    roomNo: '53001',
    roomName: 'China',
    description: 'Ship to China Nely warehouse',
    location: 'China',
    parentRoomNo: '5300',
    sortOrder: 9,
    canDelete: true,
    defaultTask: 'Prepare for China Nely shipment'
  }
];

/**
 * Stock Wig Customer Configuration
 * All stock wigs are assigned to this customer
 */
export const STOCK_CUSTOMER_ID = 6252;
export const STOCK_CUSTOMER_NAME = 'Stock Wig';

/**
 * Order Type for stock wigs
 * Keeps same OrderType as customer orders for compatibility
 */
export const STOCK_ORDER_TYPE = 'New Wig';

/**
 * Prep room number - cannot be deleted
 */
export const PREP_ROOM_NO = '9001';

/**
 * Order number format for stock wigs
 * Format: 25XXXXX (e.g., 2500001, 2500002)
 * - 25 = Year prefix (2025)
 * - XXXXX = Sequential number starting at 00001
 */
export const STOCK_ORDER_PREFIX_YEAR = new Date().getFullYear();

/**
 * Year prefixes used for stock wig order numbers (format: YYXXXXX, e.g. 2500001).
 * Client/customer orders use a separate, shorter numbering scheme and never
 * match these prefixes. Add the new two-digit year here each January.
 */
export const STOCK_ORDER_YEAR_PREFIXES = [25, 26, 27];

/**
 * Helper to check if an order number is a stock wig order
 * @param {string|number} orderNo - Order number to check
 * @returns {boolean} - True if stock wig order
 */
export const isStockWigOrder = (orderNo) => {
  if (!orderNo) return false;
  const orderStr = orderNo.toString();

  // Stock wig orders are 7 digits: YYXXXXX (e.g. 2500001 -> year 25, seq 00001)
  if (orderStr.length !== 7) return false;

  const yearPrefix = parseInt(orderStr.substring(0, 2));
  return STOCK_ORDER_YEAR_PREFIXES.includes(yearPrefix);
};

/**
 * Extract year from stock wig order number
 * @param {string|number} orderNo - Stock wig order number
 * @returns {number|null} - Year or null if not a stock order
 */
export const getStockWigYear = (orderNo) => {
  if (!isStockWigOrder(orderNo)) return null;

  const orderStr = orderNo.toString();
  // Extract year prefix: 25 -> 2025
  return 2000 + parseInt(orderStr.substring(0, 2));
};

/**
 * Extract sequence number from stock wig order number
 * @param {string|number} orderNo - Stock wig order number
 * @returns {number|null} - Sequence number or null if not a stock order
 */
export const getStockWigSequence = (orderNo) => {
  if (!isStockWigOrder(orderNo)) return null;

  const orderStr = orderNo.toString();
  return parseInt(orderStr.substring(2));
};

/**
 * Format stock wig order number for display
 * @param {string|number} orderNo - Stock wig order number
 * @returns {string} - Formatted display (e.g., "25-00001")
 */
export const formatStockWigOrderNo = (orderNo) => {
  if (!orderNo) return '';

  if (!isStockWigOrder(orderNo)) return orderNo.toString();

  const yearPrefix = orderNo.toString().substring(0, 2);
  const sequence = getStockWigSequence(orderNo);

  if (!sequence) return orderNo.toString();

  return `${yearPrefix}-${sequence.toString().padStart(5, '0')}`;
};

/**
 * Users allowed to access Rachel's Room
 */
export const RACHELS_ROOM_ALLOWED_USERS = [
  'didip',
  'amberrose',
  'rachelg',
  'noga'
];

/**
 * Check if user has access to Rachel's Room
 * @param {string} username - Username to check
 * @returns {boolean} - True if user has access
 */
export const hasRachelsRoomAccess = (username) => {
  if (!username) return false;
  return RACHELS_ROOM_ALLOWED_USERS.includes(username.toLowerCase());
};

/**
 * Default inventory status for new stock wigs
 */
export const DEFAULT_STOCK_STATUS = 'Pre-Assembly';

/**
 * Available inventory statuses for stock wigs
 */
export const STOCK_INVENTORY_STATUSES = [
  'Pre-Assembly',
  'Available',
  'Sold',
  'Shipped to China',
  'In Transit',
  'Received'
];
